import React, { useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';

const AssessmentsDropdown = ({
  isMobile = false,
  onMobileToggle,
  isMobileOpen,
  onCloseMobileMenu,
}) => {
  const { state, actions } = useApp();
  const { user } = useAuth();
  const dropdownRef = useRef(null);

  const closeDropdown = useCallback(() => {
    if (state.showAssessmentsDropdown) {
      actions.closeAssessmentsDropdown();
    }
  }, [state.showAssessmentsDropdown, actions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (state.showAssessmentsDropdown && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.showAssessmentsDropdown, isMobile, closeDropdown]);

  const isUser = !user?.role || user?.role === 'user';
  const isAdmin = user?.role === 'admin';
  const isConsultant = user?.role === 'consultant';

  if (isConsultant) {
    return null;
  }

  if (isMobile) {
    return (
      <div className="space-y-1" ref={dropdownRef}>
        <button
          onClick={onMobileToggle}
          className="w-full text-left text-gray-700 dark:text-gray-200 hover:text-emerald-700 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-gray-800 px-3 py-2 text-base font-medium transition-colors duration-200 flex items-center justify-between rounded-md"
        >
          Assessments
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isMobileOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isMobileOpen && (
          <div className="pl-4 space-y-1 bg-gray-50 dark:bg-gray-800 py-2 rounded-md">
            {isAdmin && (
              <Link
                to="/assessment/results"
                onClick={onCloseMobileMenu}
                className="block px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-emerald-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors rounded"
              >
                <div className="font-medium">Energy Assessment Dashboard</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  View your energy performance metrics
                </div>
              </Link>
            )}

            {isUser && (
              <>
                <Link
                  to="/assessment/self-guided"
                  onClick={onCloseMobileMenu}
                  className="block px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-emerald-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors rounded"
                >
                  <div className="font-medium">Energy Saving Assessment (ESA)</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Identify energy cost reduction opportunities
                  </div>
                </Link>
                <Link
                  to="/emha"
                  onClick={onCloseMobileMenu}
                  className="block px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-emerald-100 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors rounded"
                >
                  <div className="font-medium">Energy Management Health (EMHA)</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Assess your energy management maturity
                  </div>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={actions.toggleAssessmentsDropdown}
        className="text-gray-700 dark:text-gray-200 hover:text-emerald-700 dark:hover:text-emerald-400 px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center"
      >
        Assessments
        <svg
          className={`w-4 h-4 ml-1 transition-transform duration-200 ${
            state.showAssessmentsDropdown ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {state.showAssessmentsDropdown && (
        <div className="absolute left-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 z-50 overflow-hidden">
          <div className="py-1">
            {isAdmin && (
              <Link
                to="/assessment/results"
                className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors"
                onClick={() => actions.closeAssessmentsDropdown()}
              >
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 mr-2 mt-0.5 text-emerald-600 dark:text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <div>
                    <div className="font-medium">Energy Assessment Dashboard</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      View your energy performance metrics
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {isUser && (
              <>
                <Link
                  to="/assessment/self-guided"
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors"
                  onClick={() => actions.closeAssessmentsDropdown()}
                >
                  <div className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 mt-0.5 text-emerald-600 dark:text-emerald-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <div className="font-medium">Energy Saving Assessment (ESA)</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Identify energy cost reduction opportunities
                      </div>
                    </div>
                  </div>
                </Link>
                <Link
                  to="/emha"
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-gray-700 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors"
                  onClick={() => actions.closeAssessmentsDropdown()}
                >
                  <div className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 mt-0.5 text-blue-600 dark:text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <div>
                      <div className="font-medium">Energy Management Health (EMHA)</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Assess your energy management maturity
                      </div>
                    </div>
                  </div>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentsDropdown;


